import { format } from "date-fns";
import { TFunction } from "i18next";
import { SliceState } from "../../localized/localizedApi";
import type { HrTaskAdapterContext } from "./hrTaskAdapter";
import {
  HrTask,
  HrTaskResponse,
  HrTaskRowText,
  HrTaskType,
  HrTaskContentRowType,
  HrTaskActionType,
  LogicalLinkTarget,
} from "./types";

export const adaptLegacyHrTask = (
  response: HrTaskResponse,
  { language, t, localized }: HrTaskAdapterContext,
): HrTask => {
  const date = response.date
    ? format(new Date(response.date), "d.M.yyyy")
    : null;
  const rowText = getLegacyTranslatedRowText(
    response,
    language as keyof HrTaskRowText,
    t,
    localized,
    date,
  );
  const paymentPeriod =
    response.type === HrTaskType.EmploymentHasEnded &&
    response.paymentPeriodClassId
      ? localized.schedulingClass?.[response.paymentPeriodClassId]
      : undefined;
  const rows = [rowText, paymentPeriod, response.employerName]
    .filter((text): text is string => text != null && text !== "")
    .map((text) => ({ type: HrTaskContentRowType.TEXT, text }));
  const target = legacyDestination(response);

  return {
    id: response.id,
    canManuallyAcknowledge: response.canManuallyAcknowledge,
    presentation: {
      title: response.rowTitle,
      rows,
    },
    actions: {
      defaultAction: target
        ? {
            type: HrTaskActionType.NAVIGATE,
            link: { target, openInNewTab: false },
          }
        : undefined,
      confirmation: {
        type: HrTaskActionType.CONFIRM_COMPLETION,
        title: `${response.rowTitle} - ${rowText}`,
        rows: [
          {
            type: HrTaskContentRowType.TEXT,
            text: t("hrTasklist.markAsDoneConfirmation"),
          },
        ],
      },
    },
  };
};

const getLegacyTranslatedRowText = (
  task: HrTaskResponse,
  language: keyof HrTaskRowText,
  t: TFunction,
  localized: SliceState,
  date: string | null,
): string => {
  switch (task.type) {
    case HrTaskType.CertificateExpiring: {
      const translatedCertificate = getTranslatedCertificate(
        localized,
        task.aggregateDetailId,
      );

      return translatedCertificate
        ? t(`hrTasklist.${HrTaskType.CertificateExpiring}`, {
            certificate: translatedCertificate,
            date,
          })
        : t(`hrTasklist.CertificateExpiringFallback`, { date });
    }
    case HrTaskType.EmploymentEnding:
      return t(`hrTasklist.${HrTaskType.EmploymentEnding}`, { date });
    case HrTaskType.EmploymentHasEnded:
      return t(`hrTasklist.${HrTaskType.EmploymentHasEnded}`, { date });
    case HrTaskType.WorkPermitExpiring:
      return t(`hrTasklist.${HrTaskType.WorkPermitExpiring}`, { date });
    case HrTaskType.ProbationEnding:
      return t(`hrTasklist.${HrTaskType.ProbationEnding}`, { date });
    case HrTaskType.CertificateApprovalNeeded: {
      const translatedCertificate = getTranslatedCertificate(
        localized,
        task.aggregateDetailId,
      );
      return translatedCertificate
        ? t(`hrTasklist.${HrTaskType.CertificateApprovalNeeded}`, {
            certificate: translatedCertificate,
          })
        : t(`hrTasklist.CertificateApprovalNeededFallback`);
    }
    case HrTaskType.WorkAgreementNotAccepted:
      return t(`hrTasklist.${HrTaskType.WorkAgreementNotAccepted}`);
    case HrTaskType.CustomerAnswerToIntroducedTalent:
      return task.rowText[language];
    case HrTaskType.OrderExpired:
      return task.rowText[language];
    default:
      // Types without a dedicated branch fall back to the backend-provided rowText.
      return task.rowText?.[language] ?? "";
  }
};

const getTranslatedCertificate = (
  localized: SliceState,
  aggregateDetailId?: string,
) => (aggregateDetailId ? localized.certificate?.[aggregateDetailId] : "");

const legacyDestination = (
  task: HrTaskResponse,
): LogicalLinkTarget | undefined => {
  switch (task.type) {
    case HrTaskType.WorkAgreementNotAccepted:
      if (task.aggregateDetailId && task.aggregateId) {
        return {
          type: "WORK_AGREEMENT_EDIT",
          bundleId: task.aggregateDetailId,
          workAgreementId: task.aggregateId,
        };
      }
      break;
    case HrTaskType.CustomerAnswerToIntroducedTalent:
    case HrTaskType.OrderExpired:
      return { type: "ORDER", bundleId: task.aggregateId };
  }
  const talentId = task.talentId;
  if (talentId == null) return undefined;
  if (
    task.type === HrTaskType.EmploymentEnding ||
    task.type === HrTaskType.EmploymentHasEnded
  ) {
    return { type: "TALENT_EMPLOYMENTS", talentId };
  }
  return { type: "TALENT_PROFILE", talentId };
};
